import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Card, CardContent, CardActions, Dialog, Avatar, useMediaQuery, Chip, TextField, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Add, Visibility } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {createTenantUserAction, clearCreateTenantUserAction, getAllTenantUserAction} from '../../store/actions';
import toast from 'react-hot-toast';
import { InputAdornment, IconButton } from "@mui/material";
import VisibilityOff from "@mui/icons-material/VisibilityOff";


function SuperAdminUserManagement() {
  const theme = useTheme();
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    adminPhoneNumber: '',
    companyAddress: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    companyLogo: '',
    adminPassword: '',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);

const handleTogglePassword = () => {
  setShowPassword((prev) => !prev);
};

  const {successData, loading, error, errorInfo} = useSelector((state) => state.createTenantUserReducer);
  const { successData: allUsers, loading: loadingUsers, error: errorUsers, errorInfo: errorInfoUsers } = useSelector((state) => state.getAllTenantUserReducer);
  // Dynamically derived tenants list from API response
  const users = Array.isArray(allUsers?.data?.tenants) ? allUsers.data.tenants : [];

  const handleAddUser = () => {
    setErrors({});
    setFormData({
      companyName: '',
      companyEmail: '',
      companyPhone: '',
      adminEmail: '',
      adminFirstName: '',
      adminLastName: '',
      adminPhoneNumber: '',
      companyAddress: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
      companyLogo: '',
      adminPassword: '',
      notes: '',
    });
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleFormSubmit = () => {
    if (!validateForm()) return;
    const storedId = localStorage.getItem('userDetails') ? JSON.parse(localStorage.getItem('userDetails')).id : null;
    const superAdminId = storedId && /^\d+$/.test(storedId) ? Number(storedId) : storedId;
    const payload = {
      superAdminId,
      companyName: formData.companyName,
      companyEmail: formData.companyEmail,
      companyPhone: formData.companyPhone,
      adminEmail: formData.adminEmail,
      adminFirstName: formData.adminFirstName,
      adminLastName: formData.adminLastName,
      adminPhoneNumber: formData.adminPhoneNumber,
      companyAddress: formData.companyAddress || '',
      city: formData.city || '',
      state: formData.state || '',
      country: formData.country || '',
      zipCode: formData.zipCode || '',
      companyLogo: formData.companyLogo || '',
      adminPassword: formData.adminPassword || '',
      notes: formData.notes || '',
    };
    dispatch(createTenantUserAction(payload));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateEmail = (email) => {
    if (!email) return false;
    const re = /[^\s@]+@[^\s@]+\.[^\s@]+/;
    return re.test(String(email).toLowerCase());
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'companyName',
      'companyEmail',
      'companyPhone',
      'adminEmail',
      'adminFirstName',
      'adminLastName',
      'adminPhoneNumber',
    ];

    requiredFields.forEach((field) => {
      if (!formData[field] || String(formData[field]).trim() === '') {
        newErrors[field] = 'This field is required.';
      }
    });

    if (formData.companyEmail && !validateEmail(formData.companyEmail)) {
      newErrors.companyEmail = 'Enter a valid email address.';
    }
    if (formData.adminEmail && !validateEmail(formData.adminEmail)) {
      newErrors.adminEmail = 'Enter a valid email address.';
    }
    if (formData.adminPassword && formData.adminPassword.length < 8) {
      newErrors.adminPassword = 'Password must be at least 8 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getSubscriptionChipColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'expired':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const handleViewUser = (userId) => {
    navigate('/superAdmin/userDetailsView', { state: { userId } });
  };

  useEffect(() => {
    if (!loading && successData) {
      toast.success('User created successfully');
      setOpenAddDialog(false);
      setFormData({
        companyName: '',
        companyEmail: '',
        companyPhone: '',
        adminEmail: '',
        adminFirstName: '',
        adminLastName: '',
        adminPhoneNumber: '',
        companyAddress: '',
        city: '',
        state: '',
        country: '',
        zipCode: '',
        companyLogo: '',
        adminPassword: '',
        notes: '',
      });
      setErrors({});
       dispatch(
    getAllTenantUserAction({
      endPoint: `tenant/super-admin/${JSON.parse(localStorage.getItem('userDetails')).id}`,
    })
  );
      dispatch(clearCreateTenantUserAction());
    }

    if (!loading && error) {
      toast.error(errorInfo.message || 'Failed to create user');
      dispatch(clearCreateTenantUserAction());
    }
  }, [loading, successData, error, errorInfo, dispatch]);


  useEffect(() => {
    dispatch(getAllTenantUserAction({
      endPoint: `tenant/super-admin/${JSON.parse(localStorage.getItem('userDetails')).id}`,
    }));
  }, [dispatch]);

  return (
    <Box
      sx={{
        ml: { xs: 2, sm: 4, md: 35, lg: 36 },
        mr: { xs: 2, sm: 4, md: 8, lg: 12 },
        mt: { xs: 8, sm: 10, md: 12, lg: 15 },
        mb: { xs: 4, sm: 6, md: 8 },
        width: 'auto',
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >      <Box
      sx={{
        display: 'flex',
        justifyContent: { xs: 'center', sm: 'space-between' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        mb: 3,
      }}>
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, marginBottom: { xs: 2, sm: 0 } }}>
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddUser}
          sx={{
            borderRadius: '8px',
            backgroundColor: '#2563EB',
            padding: { xs: '8px 16px', sm: '10px 20px' },
          }}
        >
          Add New User
        </Button>
      </Box>

      <Grid container spacing={3}>
        {loadingUsers && (
          <Grid item xs={12}>
            <Typography variant="body1">Loading tenants...</Typography>
          </Grid>
        )}
        {!loadingUsers && errorUsers && (
          <Grid item xs={12}>
            <Typography variant="body1" color="error">{errorInfoUsers?.message || 'Failed to load tenants'}</Typography>
          </Grid>
        )}
        {!loadingUsers && !errorUsers && users.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body1">No tenants found.</Typography>
          </Grid>
        )}
        {!loadingUsers && !errorUsers && users.map((user) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
            <Card sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 3,
              maxWidth: '600px',
              width: '200px',
              borderRadius: '12px',
              transition: 'transform 0.2s',
              position: 'relative',
              '&:hover': {
                transform: { xs: 'none', sm: 'translateY(-5px)' },
                boxShadow: 6,
              }
            }}>
              {/* Subscription Status Badge */}
              <Chip
                label={user.status || 'Unknown'}
                color={getSubscriptionChipColor(user.status)}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 1,
                  fontWeight: 'bold',
                }}
              />

              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                  {user?.logoUrl ? (
                    <Avatar
                      src={user.logoUrl}
                      sx={{
                        width: 64,
                        height: 64,
                        mb: 1
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: '#2563EB',
                        mb: 1
                      }}
                    >
                      {getInitials(user.companyName)}
                    </Avatar>
                  )}
                  <Typography variant="h6" component="div" align="center">
                    <strong>{user.companyName || 'N/A'}</strong>
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <strong>Email:</strong>&nbsp;{user.companyEmail || user.adminEmail || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <strong>Status:</strong>&nbsp;{user.status || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <strong>Created:</strong>&nbsp;{formatDate(user.createdAt)}
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={() => handleViewUser(user.id)}
                  size="medium"
                  sx={{
                    borderRadius: '8px', borderColor: '#2563EB', color: '#2563EB',
                    '&:hover': {
                      backgroundColor: '#EEF5FF',
                      borderColor: '#2563EB',
                    },
                  }}
                >
                  View
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        fullScreen={useMediaQuery(theme.breakpoints.down('sm'))}
      >
        <DialogTitle>Add User</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {/* Required Fields */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                size="small"
                fullWidth
                required
                error={Boolean(errors.companyName)}
                helperText={errors.companyName}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Company Email"
                name="companyEmail"
                type="email"
                value={formData.companyEmail}
                onChange={handleInputChange}
                size="small"
                fullWidth
                required
                error={Boolean(errors.companyEmail)}
                helperText={errors.companyEmail}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Company Phone"
                name="companyPhone"
                value={formData.companyPhone}
                onChange={handleInputChange}
                size="small"
                fullWidth
                required
                error={Boolean(errors.companyPhone)}
                helperText={errors.companyPhone}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Admin Email"
                name="adminEmail"
                type="email"
                value={formData.adminEmail}
                onChange={handleInputChange}
                size="small"
                fullWidth
                required
                error={Boolean(errors.adminEmail)}
                helperText={errors.adminEmail}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Admin First Name"
                name="adminFirstName"
                value={formData.adminFirstName}
                onChange={handleInputChange}
                size="small"
                fullWidth
                required
                error={Boolean(errors.adminFirstName)}
                helperText={errors.adminFirstName}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Admin Last Name"
                name="adminLastName"
                value={formData.adminLastName}
                onChange={handleInputChange}
                size="small"
                fullWidth
                required
                error={Boolean(errors.adminLastName)}
                helperText={errors.adminLastName}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Admin Phone Number"
                name="adminPhoneNumber"
                value={formData.adminPhoneNumber}
                onChange={handleInputChange}
                size="small"
                fullWidth
                required
                error={Boolean(errors.adminPhoneNumber)}
                helperText={errors.adminPhoneNumber}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>

            {/* Optional Fields */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Company Address"
                name="companyAddress"
                value={formData.companyAddress}
                onChange={handleInputChange}
                size="small"
                fullWidth
                error={Boolean(errors.companyAddress)}
                helperText={errors.companyAddress}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="City"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                size="small"
                fullWidth
                error={Boolean(errors.city)}
                helperText={errors.city}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="State"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                size="small"
                fullWidth
                error={Boolean(errors.state)}
                helperText={errors.state}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                size="small"
                fullWidth
                error={Boolean(errors.country)}
                helperText={errors.country}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Zip Code"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
                size="small"
                fullWidth
                error={Boolean(errors.zipCode)}
                helperText={errors.zipCode}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}

              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Admin Password (min 8)"
                name="adminPassword"
                // type="password"
                type={showPassword ? "text" : "password"}
                value={formData.adminPassword}
                onChange={handleInputChange}
                size="small"
                fullWidth
                error={Boolean(errors.adminPassword)}
                helperText={errors.adminPassword}
                sx={{ minWidth: { xs: 310, sm: 310, md: 275 } }}
                InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            onClick={handleTogglePassword}
            edge="end"
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    }}

              />
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              size="small"
              fullWidth
              multiline
              minRows={3}
              error={Boolean(errors.notes)}
              helperText={errors.notes}
              sx={{ minWidth: { xs: 310, sm: 310, md: 275 }, mt:2 }}

            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} color="inherit">Cancel</Button>
          <Button variant="contained" sx={{ backgroundColor: '#2563EB' }} onClick={handleFormSubmit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
}

export default SuperAdminUserManagement;
